import {isFeatureEnabled} from 'feature-flags';
import {Flag, Rank, Separator, Tooltip} from 'platform/components';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Right,
  Scroll,
  Show,
  Text,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber, useLocale} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {memo} from 'react';

import {isNotNil} from 'ramda';
import {floor} from 'ramda-adjunct';

import {SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import {i18n} from '@dms/i18n';
import {ColorCircle} from '@dms/shared';

import {
  CurrencyCodeType,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {ActionButtonsVehicle} from '../../../components/ActionButtonsVehicle/ActionButtonsVehicle';
import {Clamp} from '../../../components/Clamp/Clamp';
import {ClassifiedsSoldFlag} from '../../../components/ClassifiedsSoldFlag/ClassifiedsSoldFlag';
import {CountryFlag} from '../../../components/CountryFlag/CountryFlag';
import {HitlistBlacklistFlag} from '../../../components/HitlistBlacklistFlag/HitlistBlacklistFlag';
import {InactiveSourcingAd} from '../../../components/InactiveSourcingAd/InactiveSourcingAd';
import {getFirstScrapedPrice} from '../../../components/PriceChangesPopper/getFirstScrapedPrice';
import {PriceChangesPopper} from '../../../components/PriceChangesPopper/PriceChangesPopper';
import {HighlightFlag} from '../../../components/VehicleHighlights/components/HighlightFlag';
import {getInteriorMaterialHighlights} from '../../../components/VehicleHighlights/utils/getInteriorMaterialHighlights';
import {VehicleImage} from '../../../components/VehicleImage/VehicleImage';
import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {useApiEnums} from '../../../hooks/useApiEnums';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {FeaturesType} from '../../../types/FeaturesType';
import {decimalToPercentRank} from '../../../utils/decimalToPercentRank';
import {getDetailFirstLine, getDetailFirstLineForTooltip} from '../../../utils/getDetailFirstLine';
import {getSuspiciousReasonsLabel} from '../../../utils/getSuspiciousReasonsLabel';
import {getVehicleDetailPosition} from '../../../utils/getVehicleDetailPosition';
import {isAuctionVehicle} from '../../../utils/isAuctionVehicle';
import {COMPARISON_HEADER_ATTRIBUTE} from '../constants/COMPARISON_HEADER_ATTRIBUTE';
import {COMPARISON_STICKY_POSITION} from '../constants/COMPARISON_STICKY_POSITION';
import {useComparisonHover} from '../hooks/useComparisonHover';
import {formatBodyColor} from '../utils/formatBodyColor';
import {MinMaxValues} from '../utils/getMinMaxValues';
import {ComparisonBlock} from './ComparisonBlock';
import {ComparisonRow} from './ComparisonRow';
import {ComparisonVehicleHeader} from './ComparisonVehicleHeader';

export interface VehicleColumnProps extends TestIdProps {
  vehicle: SourcingVehicleDetailResponseBody;
  minMaxValues: MinMaxValues;
  isInactiveAd: boolean;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
const VehicleColumnComponent = (props: VehicleColumnProps) => {
  const {hasUnifiedSurfaces} = useComparisonHover();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const {language} = useLocale();
  const {getEnum} = useApiEnums();
  const colorEnum = getEnum('body_color');
  const sourcingType = useSourcingType();
  const isClassifieds = sourcingType === SourcingType.CLASSIFIEDS;
  const isAuctions = sourcingType === SourcingType.AUCTIONS;
  const isOriginCountryFilterEnabled = isFeatureEnabled(
    featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
  );

  const {sourcingVehicle, summary, vehicleSummary} = props.vehicle;
  const {minPrice, maxPrice, minMileage, maxMileage, minYear, maxYear} = props.minMaxValues;

  const vehiclePosition = getVehicleDetailPosition(
    summary.position ?? sourcingVehicle.extraData?.marketPosition,
    summary.positionFrom ?? sourcingVehicle.extraData?.marketPositionCount,
    sourcingVehicle.pricePercentile
  );
  const originalPrice = sourcingVehicle.price?.originalWithVat;
  const originalCurrency = sourcingVehicle.price?.originalCurrency;
  const currency = sourcingVehicle.price?.currency;
  const originalCurrencyCode = originalCurrency?.key as CurrencyCodeType;
  const convertedPrice = summary.original;
  const convertedCurrencyCode = currency?.key as CurrencyCodeType;
  const firstScrapedPrice = getFirstScrapedPrice(sourcingVehicle, convertedPrice);
  const priceColor = match<number | Nullish, ThemeColorTextPath | undefined>(
    maxPrice !== minPrice ? originalPrice : undefined
  )
    .with(maxPrice, () => 'danger')
    .with(minPrice, () => 'success')
    .otherwise(() => undefined);
  const mileageColor = match<number | Nullish, ThemeColorTextPath | undefined>(
    maxMileage !== minMileage ? sourcingVehicle.mileage : undefined
  )
    .with(maxMileage, () => 'danger')
    .with(minMileage, () => 'success')
    .otherwise(() => undefined);
  const yearColor = match<number | Nullish, ThemeColorTextPath | undefined>(
    maxYear !== minYear ? sourcingVehicle.year : undefined
  )
    .with(minYear, () => 'danger')
    .with(maxYear, () => 'success')
    .otherwise(() => undefined);
  const color = colorEnum.find((color) => color?.key === sourcingVehicle.color?.color?.key);
  const interiorMaterialHighlights = getInteriorMaterialHighlights(
    sourcingVehicle.interiorMaterial
  );
  const highlightedOrPremiumFeatures = Object.values(props.vehicle.features ?? {}).filter(
    ({isAvailable, isHighlight}: FeaturesType) => isAvailable && isHighlight
  );
  const suspiciousReasons = getSuspiciousReasonsLabel(sourcingVehicle?.suspiciousReason);
  const isInactiveAd = props.isInactiveAd;

  return (
    <>
      <Box
        position={COMPARISON_STICKY_POSITION}
        top={0}
        zIndex={hasUnifiedSurfaces ? 'CONTENT' : undefined}
        backgroundColor={isInactiveAd ? 'palettes.neutral.30.100' : 'general.white'}
      >
        <ComparisonVehicleHeader {...{[COMPARISON_HEADER_ATTRIBUTE]: true}}>
          <InactiveSourcingAd isDisabled={isInactiveAd}>
            <VehicleImage vehicle={props.vehicle} hasUnifiedSurfaces={hasUnifiedSurfaces} />
          </InactiveSourcingAd>
          <Box padding={2}>
            <VStack spacing={2}>
              <Right>
                <ActionButtonsVehicle
                  vehicle={props.vehicle}
                  data-testid={suffixTestId('actionButtonsVehicle', props)}
                />
              </Right>
              <Clamp lines={3}>
                <Box height={15}>
                  <FloatLeft>
                    <Show when={isClassifieds}>
                      <Box paddingRight={1}>
                        <ClassifiedsSoldFlag
                          sourcingVehicle={sourcingVehicle}
                          data-testid={suffixTestId('sold', props)}
                        />
                      </Box>
                    </Show>
                  </FloatLeft>
                  <FloatLeft>
                    <Box paddingRight={1}>
                      <Show when={vehicleSummary?.comment}>
                        <Tooltip placement="top" label={vehicleSummary?.comment}>
                          <Icon size={4} value="communication/comment" />
                        </Tooltip>
                      </Show>
                    </Box>
                  </FloatLeft>
                  <FloatRight>
                    <Box height={5}>
                      <HStack height="100%" align="center" spacing={1}>
                        <Show
                          whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                          when={sourcingVehicle.suspicious}
                        >
                          <Tooltip label={suspiciousReasons}>
                            <Icon value="alert/warning" color="severity.warning" size={5} />
                          </Tooltip>
                        </Show>
                        <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
                      </HStack>
                    </Box>
                  </FloatRight>
                  <Tooltip label={getDetailFirstLineForTooltip(sourcingVehicle)}>
                    <Heading size={5} color={isInactiveAd ? 'tertiary' : undefined}>
                      {getDetailFirstLine(sourcingVehicle)}
                    </Heading>
                  </Tooltip>
                </Box>
              </Clamp>
            </VStack>
          </Box>
          <Separator spacing={0} />
        </ComparisonVehicleHeader>
      </Box>
      <ComparisonBlock>
        <ComparisonRow rowKey="position">
          <Text size="xSmall">
            {vehiclePosition[0]} {vehiclePosition[1]}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="adPrice">
          <Box height={isFeatureEnabled(featureFlags.SOURCING_PRICE_CHANGES) ? 9 : undefined}>
            <VStack spacing={1}>
              <Show
                when={
                  isNotNil(convertedPrice) &&
                  isNotNil(firstScrapedPrice) &&
                  firstScrapedPrice !== convertedPrice
                }
                whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}
              >
                <HStack spacing={1} align="center">
                  <PriceChangesPopper
                    vehicle={sourcingVehicle}
                    currentPrice={convertedPrice ?? 0}
                    currency={convertedCurrencyCode}
                    popperPlacement="top-start"
                  />
                  <Text size="xxSmall" color="tertiary" strikethrough>
                    {formatCurrency(firstScrapedPrice ?? 0, convertedCurrencyCode)}
                  </Text>
                </HStack>
              </Show>
              <HStack spacing={1} align="center">
                <Show when={isNotNil(convertedPrice) && sourcingVehicle.price?.vatReclaimable}>
                  <Tooltip
                    placement="top"
                    label={i18n.t('entity.vehicle.labels.tooltipVatReclaimable')}
                  >
                    <Flag
                      label={i18n.t('entity.warehouse.labels.articleVat')}
                      colorScheme="black"
                      size="small"
                    />
                  </Tooltip>
                </Show>
                <Tooltip
                  placement="top"
                  label={
                    isNotNil(originalPrice)
                      ? formatCurrency(originalPrice, originalCurrencyCode)
                      : undefined
                  }
                >
                  <Text color={priceColor} size="xSmall">
                    {isNotNil(convertedPrice)
                      ? formatCurrency(convertedPrice, convertedCurrencyCode)
                      : EMPTY_PLACEHOLDER}
                  </Text>
                </Tooltip>
              </HStack>
            </VStack>
          </Box>
        </ComparisonRow>
        <ComparisonRow rowKey="rankEquipment">
          <HStack spacing={2} align="center">
            <Rank percentage={floor((summary.rank ?? 0) * 100)} />
            <Text size="xSmall">{decimalToPercentRank(summary.rank ?? undefined)}</Text>
          </HStack>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <Show when={isAuctions}>
          <ComparisonRow rowKey="registration">
            <Text size="xSmall">
              {isAuctionVehicle(props.vehicle) && props.vehicle.auctionVehicle.registrationDate
                ? formatDateTime(
                    'dateShort',
                    parseDate(props.vehicle.auctionVehicle.registrationDate)
                  )
                : EMPTY_PLACEHOLDER}
            </Text>
          </ComparisonRow>
        </Show>
        <Show when={isClassifieds}>
          <ComparisonRow rowKey="STK">
            <Text size="xSmall">
              {sourcingVehicle.technicalInspectionValidTo
                ? formatDateTime(
                    'dateShort',
                    parseDate(sourcingVehicle?.technicalInspectionValidTo)
                  )
                : EMPTY_PLACEHOLDER}
            </Text>
          </ComparisonRow>
        </Show>
        <ComparisonRow rowKey="serviceBook">
          <Text size="xSmall">
            {sourcingVehicle.features?.some((feature) => feature.key === 'FEATURE_SERVICE_BOOK')
              ? i18n.t('general.labels.yes')
              : i18n.t('general.labels.no')}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="mileage">
          <Text size="xSmall" color={mileageColor}>
            {isNotNil(sourcingVehicle.mileage)
              ? `${formatNumber(sourcingVehicle.mileage, 0)} ${i18n.t('general.metric.km')}`
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="manufactureYear">
          <Text color={yearColor} size="xSmall">
            {sourcingVehicle.year ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        {/* An array, not a wrapper: ComparisonBlock passes the striping index to its direct
            children only, and Children.map flattens arrays. */}
        {isOriginCountryFilterEnabled ? (
          [
            <ComparisonRow key="sellerCountry" rowKey="sellerCountry">
              <HStack spacing={1} align="center">
                <Show when={isNotNil(sourcingVehicle.seller?.country?.key)}>
                  <CountryFlag size={4} country={sourcingVehicle.seller?.country?.key} />
                </Show>
                <Text size="xSmall" noWrap data-testid={suffixTestId('sellerCountryRow', props)}>
                  {sourcingVehicle.seller?.country?.translation ?? EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            </ComparisonRow>,
            <ComparisonRow key="originCountry" rowKey="countryOfOrigin">
              <HStack spacing={1} align="center">
                <Show when={isNotNil(sourcingVehicle.originCountry?.key)}>
                  <CountryFlag size={4} country={sourcingVehicle.originCountry?.key} />
                </Show>
                <Text size="xSmall" noWrap data-testid={suffixTestId('originCountryRow', props)}>
                  {sourcingVehicle.originCountry?.translation ?? EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            </ComparisonRow>,
          ]
        ) : (
          <ComparisonRow rowKey="countryOfOrigin">
            <HStack spacing={1} align="center">
              <CountryFlag
                size={4}
                country={sourcingVehicle.originCountry?.key ?? sourcingVehicle.seller?.country?.key}
              />
              <Text size="xSmall" noWrap>
                {sourcingVehicle.originCountry?.translation ??
                  sourcingVehicle.seller?.country?.translation ??
                  EMPTY_PLACEHOLDER}
              </Text>
            </HStack>
          </ComparisonRow>
        )}
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow rowKey="fuelType">
          <Text size="xSmall">{sourcingVehicle.fuelType?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow rowKey="power">
          <Text size="xSmall">{sourcingVehicle.power ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow rowKey="engineCapacity">
          <Text size="xSmall">
            {formatNumber(sourcingVehicle.cubicCapacity, 0) ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="transmission">
          <Text size="xSmall">
            {sourcingVehicle.transmission?.translation ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="driveType">
          <Text size="xSmall">{sourcingVehicle.driveType?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow rowKey="originalPrice">
          <Text size="xSmall">
            {isNotNil(summary.original)
              ? formatCurrency(
                  summary.original,
                  sourcingVehicle.price?.currency?.key ?? DEFAULT_CURRENCY_CODE
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="recommendedPrice">
          <Text size="xSmall">
            {isNotNil(sourcingVehicle.extraData?.predictedPrice)
              ? formatCurrency(
                  sourcingVehicle.extraData?.predictedPrice,
                  sourcingVehicle.price?.currency?.key ?? DEFAULT_CURRENCY_CODE
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow rowKey="margin">
          <Text
            size="xSmall"
            color={(sourcingVehicle.maximalPossibleMargin ?? 0) < 0 ? 'danger' : undefined}
          >
            {isNotNil(sourcingVehicle.maximalPossibleMargin)
              ? formatCurrency(
                  sourcingVehicle.maximalPossibleMargin,
                  sourcingVehicle.price?.currency?.key ?? DEFAULT_CURRENCY_CODE
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <Show when={isClassifieds}>
          <ComparisonRow rowKey="costsPrice">
            <Text size="xSmall">
              {isNotNil(summary.vehicleCosts)
                ? formatCurrency(
                    summary.vehicleCosts,
                    sourcingVehicle.price?.currency?.key ?? DEFAULT_CURRENCY_CODE
                  )
                : EMPTY_PLACEHOLDER}
            </Text>
          </ComparisonRow>
        </Show>
        <ComparisonRow rowKey="LID">
          <Text size="xSmall">{summary.lid ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <Show when={isClassifieds}>
          <ComparisonRow rowKey="DOD">
            <Text size="xSmall">{sourcingVehicle.daysOnStock ?? EMPTY_PLACEHOLDER}</Text>
          </ComparisonRow>
        </Show>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow rowKey="bodyColor">
          <HStack align="center" spacing={1}>
            <Show when={sourcingVehicle.color?.color?.key ? sourcingVehicle.color.color.key : null}>
              {(key) => (
                <ColorCircle
                  colorLabel={color?.label}
                  colorCode={color?.colorCode}
                  needBorder={key === 'COLOR_WHITE'}
                  isDisabled
                />
              )}
            </Show>

            <Text size="xSmall" data-testid={suffixTestId('bodyColorRow', props)}>
              {formatBodyColor(sourcingVehicle.color, language) ?? EMPTY_PLACEHOLDER}
            </Text>
          </HStack>
        </ComparisonRow>
        <ComparisonRow rowKey="interiorMaterial">
          <Text size="xSmall" data-testid={suffixTestId('interiorMaterialRow', props)}>
            {sourcingVehicle.interiorMaterial?.translation ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <Show when={isClassifieds}>
          <ComparisonRow rowKey="numberOfSeats">
            <Text size="xSmall">{sourcingVehicle.numberOfSeats ?? EMPTY_PLACEHOLDER}</Text>
          </ComparisonRow>
        </Show>
        <ComparisonRow rowKey="carStyle">
          <Text size="xSmall">{sourcingVehicle.carStyle?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow rowKey="numberOfDoors">
          <Text size="xSmall">{sourcingVehicle.doorCount?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <Scroll auto height={48}>
        <ComparisonBlock hasNoSpace>
          {interiorMaterialHighlights.map((item) => (
            <ComparisonRow key={item.key} rowKey={`highlight-${item.key}`}>
              <Tooltip label={item.tooltip}>
                <HighlightFlag
                  label={item.label}
                  size="small"
                  colorScheme={item.colorScheme}
                  isSubtle={item.isSubtle}
                  icon={item.icon}
                  data-testid={suffixTestId(item.key, props)}
                />
              </Tooltip>
            </ComparisonRow>
          ))}
          {highlightedOrPremiumFeatures.map((feature) => (
            <ComparisonRow key={feature.key} rowKey={`highlight-${feature.key}`}>
              <Tooltip label={feature.translation}>
                <Text size="xSmall" color={feature.isHighlight ? 'link' : undefined} noWrap>
                  {feature.translation}
                </Text>
              </Tooltip>
            </ComparisonRow>
          ))}
        </ComparisonBlock>
      </Scroll>
      <ComparisonBlock hasNoSpace>
        {Object.values(props.vehicle.features ?? {}).map((feature: FeaturesType) => (
          <ComparisonRow key={feature.key} rowKey={`feature-${feature.key}`}>
            <Tooltip label={feature.translation}>
              <Text
                size="xSmall"
                color={match<FeaturesType, ThemeColorTextPath | undefined>(feature)
                  .with({isAvailable: false}, () => 'tertiary')
                  .with({isHighlight: true}, () => 'link')
                  .otherwise(() => undefined)}
                noWrap
              >
                {feature.translation}
              </Text>
            </Tooltip>
          </ComparisonRow>
        ))}
      </ComparisonBlock>
      <Separator spacing={0} />
    </>
  );
};

export const VehicleColumn = memo(VehicleColumnComponent);

const FloatLeft = styled.div`
  float: left;
`;
const FloatRight = styled.div`
  float: right;
  height: 100%;
  display: flex;
  align-items: flex-end;
  shape-outside: inset(calc(100% - ${({theme}) => theme.getSize(5)}) 0 0);
`;
